import {
  action,
  computed,
  observable,
} from 'mobx';
import RootStore from '../../../stores/root-store';
import { authConfig } from '../constants/authConfig';
import logger from '../../../utils/logger';
import { IndexDB } from '../../../utils/indexDB';
import type { CompanyConfig } from '../types/CompanyConfig';
import { User } from '../../../../types/api/User';
import { User as UserModel } from '../model/User';
import storage, { LocalStorage } from '../../../utils/local-storage';
import { customRequest } from '../../../utils/custom-request/custom-request';
import { SysdyneIdPlugin } from '../../../../libs/sysdyne-id-client/react/types';
import { setupSysdyneIdPlugin } from '../setup-sysdyne-id-plugin';
import {
  AuthError,
  AuthErrorType,
  SysdyneIdUser,
} from '@sysdynetechnologies/sysdyne-id-client-frontend';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { NETWORK_EVENT } from '../../../stores/connection-store';
// @ts-ignore
import { createBrowserHistory } from 'history';
import {
  asyncForEach,
  waitFor,
} from '../../../utils/async';
import {
  TokenRefreshManager,
  type TokenInfo,
} from '../managers/token-refresh-manager';
import { logTokenRefreshEvent } from '../utils/log-token-refresh-event';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FEATURE_SHOW_STARTUP_PAGE = isFeatureEnabled('show_startup_page');
const OFFLINE_REQUESTS_KEY = 'offlineRequests';
const LIFE_TIME_FOR_OFFLINE_REQUESTS = 24 * 60 * 60 * 1000; //24h
const DEV_CACHE_TTL = 30 * 60 * 1000; // 30 minutes cache for development

class NewAuthStore {
  sysdyneIdPlugin: SysdyneIdPlugin;
  storeDB: IndexDB;
  storage: LocalStorage;
  rootStore: RootStore;
  private tokenRefreshManager: TokenRefreshManager;
  private history = createBrowserHistory();

  @observable
    isAuthenticated = false;
  @observable
    authenticationError: string | null = null;
  @observable
    authToken: string | null = null;
  @observable
    refreshToken: string | null = null;
  @observable
    companyConfig: CompanyConfig | any = {};
  @observable
    user: User | null = null;
  @observable
    isLoading = true;
  @observable
    initAlreadyCompleted = false;
  @observable
    initInProgress = false;
  @observable
    tokenExpired = false;
  @observable
    tokenExpiresAt: number | null = null;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore;
    this.storage = storage;
    this.storeDB = new IndexDB();
    this.sysdyneIdPlugin = setupSysdyneIdPlugin(
      authConfig,
      this.handleOnAuthStateChanged.bind(this),
      this.handleOnPostRedirectNavigateRequest.bind(this),
      this.handleOnRedirectError.bind(this),
    );

    this.tokenRefreshManager = new TokenRefreshManager(
      this.sysdyneIdPlugin,
      this.rootStore,
      this.handleTokenRefreshed.bind(this),
      this.handleTokenExpired.bind(this),
      this.authToken && this.refreshToken && this.tokenExpiresAt ? {
        accessToken: this.authToken,
        refreshToken: this.refreshToken,
        expiresAt: this.tokenExpiresAt,
      } : undefined,
    );

    // When the startup page is enabled,
    // the auth initialization is handled by the startup page
    // Otherwise, we need to listen for the connection event to init the auth
    if (!FEATURE_SHOW_STARTUP_PAGE) {
      // Init AFTER the connection store has checked the connection
      window.addEventListener(NETWORK_EVENT.CONNECTION_GOOD, this.handleConnectionEvent.bind(this));
      window.addEventListener(NETWORK_EVENT.CONNECTION_SLOW, this.handleConnectionEvent.bind(this));
      window.addEventListener(NETWORK_EVENT.CONNECTION_ERROR, this.handleConnectionEvent.bind(this));
    }
  }

  @action
  private handleTokenRefreshed(tokenInfo: TokenInfo) {
    this.authToken = tokenInfo.accessToken;
    this.refreshToken = tokenInfo.refreshToken;
    this.tokenExpiresAt = tokenInfo.expiresAt;
    this.tokenExpired = false;
  }

  @action
  private handleTokenExpired() {
    this.tokenExpired = true;
  }

  async handleOnPostRedirectNavigateRequest(url: string) {
    this.history.replace(url);
    // After the SSO redirect – init the auth
    // This will automatically move user past the startup page
    await this.init();
  }

  handleOnAuthStateChanged(authenticated: boolean, user: SysdyneIdUser | undefined) {
    if (authenticated) {
      this.isAuthenticated = true;
      this.isLoading = false;
    }
  }

  handleOnRedirectError(error: unknown) {
    this.authenticationError = error instanceof Error ? error.message : 'An error occurred during authentication';
    this.isAuthenticated = false;
    this.isLoading = false;
    logger.error('Error during SSO redirect', error);
  }

  async handleConnectionEvent() {
    await this.init();
  }

  @computed
  get apiURL() {
    const companyConfig = this.companyConfig as CompanyConfig;
    return companyConfig?.apiServer ?
      `https://${companyConfig.apiServer}` :
      `${API_BASE_URL}`;
  }

  @action
  async init() {
    if (this.initAlreadyCompleted || this.initInProgress) {
      return;
    }
    this.initInProgress = true;
    await this.login();
    if (!this.isAuthenticated) {
      return;
    }

    await this.fetchCompanyConfig();
    await this.checkProductionUrlAndRedirect();
    await this.fetchCurrentUser();

    this.isLoading = false;
    this.initInProgress = false;
    this.initAlreadyCompleted = true;
  }

  @action
  async checkProductionUrlAndRedirect() {
    if (!this.companyConfig?.environment) {
      return;
    }

    const currentUrl = window.location.href;
    const prodUrl = 'https://cb.sysdyne.cloud';
    const gaUrl = 'https://ga.cb.sysdyne.cloud';
    const isGaApi = this.companyConfig?.environment === 'ga';

    if (currentUrl.includes(prodUrl) && isGaApi) {
      window.location.replace(gaUrl);
      this.removeTokenFromLocalStorage();
    } else if (currentUrl.includes(gaUrl) && !isGaApi) {
      window.location.replace(prodUrl);
      this.removeTokenFromLocalStorage();
    }
  }

  removeTokenFromLocalStorage() {
    Object.keys(localStorage).forEach(k => /token/i.test(k) && localStorage.removeItem(k));
  }

  @action
  private updateTokenInfo(accessToken: string, refreshToken: string, expiresAt: number) {
    this.authToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiresAt = expiresAt;
    this.tokenExpired = false;

    // Update the token refresh manager with the new token info
    this.tokenRefreshManager.updateTokenInfo({
      accessToken,
      refreshToken,
      expiresAt,
    });
  }

  @action
  async login(username?: string) {
    const { isOnline } = this.rootStore.connectionStore;

    if (!isOnline) {
      logger.log('Proceeding with offline login');
      if (!username) {
        logger.error('You are offline and no username was provided. Can\'t login');
        this.isLoading = false;
        return;
      }
      return await this.offlineLogin(username);
    }

    this.isLoading = true;
    let authToken;
    try {
      authToken = await this.sysdyneIdPlugin.getToken();
    } catch (error) {
      if (
        error instanceof AuthError &&
        error.type === AuthErrorType.NO_ACTIVE_TOKEN
      ) {
        // No active token, proceed with login
      } else {
        await this.tokenRefreshManager.handleTokenRefreshError(error);
        return;
      }
    }
    const isOnRedirectPage = this.sysdyneIdPlugin.isOnRedirectPage();
    if (
      authToken?.accessToken &&
      authToken?.refreshToken &&
      authToken?.expiresAt
    ) {
      this.isAuthenticated = true;
      this.updateTokenInfo(
        authToken.accessToken,
        authToken.refreshToken,
        authToken.expiresAt,
      );
      await this.rootStore.newAuthStore.fetchCompanyConfig();
      this.isLoading = false;
    } else if (!isOnRedirectPage) {
      // In case of redirect page – plugin handles it itself
      await this.sysdyneIdPlugin.loginWithRedirect();
    }
    // Don't change .isLoading to false here.
    // At this point, the user is being redirected and the method
    // will be called again when the user is redirected back to the app.
  }

  @action
  async logout() {
    this.isLoading = true;
    this.isAuthenticated = false;
    this.authToken = null;
    this.refreshToken = null;
    await this.sysdyneIdPlugin.logout();
  }

  @action
  async attemptTokenRefresh() {
    if (!this.authToken || !this.refreshToken) {
      logTokenRefreshEvent('attempt-refresh-error', {
        userId: this.user?.id,
        username: this.user?.name,
        plant: this.rootStore.locationsStore.currentPlant?.description,
        hasAuthToken: !!this.authToken,
        hasRefreshToken: !!this.refreshToken,
        error: 'Auth token is not set. Ignoring refresh request',
      });
      throw new Error('Auth token is not set. Ignoring refresh request');
    }
    await this.tokenRefreshManager.attemptTokenRefresh();
  }

  @action
  async offlineLogin(username: string) {
    const isValid = await this.isValidOffline(username);
    if (!isValid) {
      throw new Error('Invalid username');
    }
    logger.log('Offline login successful');
    this.isAuthenticated = true;
    this.isLoading = false;
    await this.fetchCompanyConfig();
    await this.fetchCurrentUser();
    await this.rootStore.locationsStore.getLocations();
  }

  @action
  async getOfflineLoginData(): Promise<User|null> {
    const url = '/api/account/me';
    await this.storeDB.init();
    return await this.storeDB.getByKey(url);
  }

  @action
  async doesOfflineLoginDataExist(): Promise<boolean> {
    const data = await this.getOfflineLoginData();
    return !!data?.email;
  }

  async isValidOffline(username: string): Promise<boolean> {
    const data = await this.getOfflineLoginData();
    logger.log('Checking offline data. Email: ', data?.email);
    return !!username && data?.email === username;
  }

  @action
    fetchCompanyConfig = async () => {
      this.isLoading = true;
      logger.log('Fetching company config (new auth)');
      this.companyConfig = await this.makeAuthorizedRequest(
        '/api/account/me/company/configuration',
      ) as CompanyConfig;
      this.isLoading = false;
    };

  @action
    fetchCurrentUser = async () => {
      this.isLoading = true;

      const user = await this.makeAuthorizedRequest(
        '/api/account/me',
      ) as unknown as User;
      this.user = new UserModel(user);

      // Update user info in the token refresh manager
      if (this.user) {
        this.tokenRefreshManager.updateUser(this.user);
      }

      this.isLoading = false;
      return user;
    };

  @action
    postFormData = async (
      url: string,
      formData: FormData,
    ) => {
      const requestUrl = `${this.apiURL}${url}`;

      return fetch(requestUrl, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${this.authToken}`,
        },
      });
    };

  @action
    sendOfflineRequests = async (): Promise<void> => {
      await this.storeDB.init();
      let requests = await this.storeDB.getByKey(OFFLINE_REQUESTS_KEY) || [];
      let removedIndexes:number[] = [];

      logger.log('Sending offline requests', requests.length);

      await asyncForEach(requests, async (request: {
          url: string;
          params: Record<string, string | number>;
          method: string;
          headers: any;
          options: any;
          date: number;
        }, i: any) => {
        const {
          url,
          params,
          method,
          headers,
          options,
          date,
        } = request;

        const dateTimestamp = new Date(date).getTime();

        if (date && Date.now() - dateTimestamp < LIFE_TIME_FOR_OFFLINE_REQUESTS) {
          logger.log('Sending offline request:', i, request);
          try {
            await this.makeAuthorizedRequest(url, params, method, headers, options);
            removedIndexes.push(i);
          } catch (err) {
            logger.error(err);
          }
        } else {
          logger.log('Removing offline request because data is older than 24h', i, request);
          removedIndexes.push(i);
        }

        await waitFor(100);

      });

      requests = requests.filter((_:any, index:number):boolean => !removedIndexes.includes(index));
      logger.debug('removedIndexes', removedIndexes, requests);

      await this.storeDB.save(OFFLINE_REQUESTS_KEY, requests);
    };

  private offlineRequestsSaveQueue: Promise<void> = Promise.resolve();

  saveOfflineRequest = async (
    url: string,
    params?: Record<string, unknown>,
    method: string = 'PUT',
    headers?: HeadersInit,
    options: RequestInit = {},
  ): Promise<unknown> => {
    const previousSave = this.offlineRequestsSaveQueue;

    let resolveQueue!: () => void;
    this.offlineRequestsSaveQueue = new Promise<void>(resolve => {
      resolveQueue = resolve;
    });

    await previousSave;

    try {
      await this.storeDB.init();

      const requests = await this.storeDB.getByKey(OFFLINE_REQUESTS_KEY) || [];
      const index = requests.findIndex((v: any) => v.url === url && v.method === method);
      const oneRequestOnly = index > -1 && !url.endsWith('/use');

      if (oneRequestOnly) {
        requests[index] = {
          ...requests[index],
          params,
          options,
          headers,
        };
      } else {
        requests.push({
          url,
          params,
          method,
          headers,
          options,
          date: Date.now(),
        });
      }

      logger.log(
        `Saving offline ${url}`,
        method,
        params,
        headers,
        options,
      );

      await this.storeDB.save(OFFLINE_REQUESTS_KEY, requests);

      return { ...params };
    } finally {
      resolveQueue();
    }
  };

  @action
    purgeCacheDB = async (): Promise<void> => {
      try {
        await this.storeDB.init();
        await this.storeDB.clear();
        logger.log('Cache DB purged successfully');
      } catch (error) {
        logger.error('Error purging cache DB', error);
        throw error;
      }
    };

  @action
    makeAuthorizedRequest = async (
      url: string,
      params?: Record<string, unknown>,
      method: string = 'GET',
      headers?: HeadersInit,
      options: RequestInit = {},
    ): Promise<unknown> => {
      const { notifierStore, batchStore } = this.rootStore;
      const { isOnline } = this.rootStore.connectionStore;

      if (!isOnline) {
        if (!method || method === 'GET') {
          logger.warn(`You are offline. Serving from indexDB for ${url}`);
          return this.storeDB.init().then(() => this.storeDB.getByKey(url));
        } else if (batchStore.canEmit) {
          return this.saveOfflineRequest(url, params, method, headers, options);
        } else {
          throw new Error('Can not send data offline');
        }
      }

      // Development mode: Check cache first with TTL (before making API call)
      if (
        process.env.REACT_APP_LOAD_FROM_CACHE === 'true'
        && process.env.NODE_ENV === 'development'
        && isOnline
        && (!method || method === 'GET')
      ) {
        try {
          await this.storeDB.init();
          const cachedData = await this.storeDB.getByKeyWithTTL(url, DEV_CACHE_TTL);
          if (cachedData !== null) {
            logger.log(`[DEV] Serving from cache: ${url}`);
            return cachedData;
          }
        } catch (error) {
          logger.warn(`[DEV] Cache check failed for ${url}:`, error);
          // Continue with normal API request if cache check fails
        }
      }

      if (this.tokenExpired && isOnline) {
        notifierStore.showError('Your session has expired. Please log in again.', 10000);
        throw new Error('Your token has expired');
      }

      if (!this.authToken) {
        notifierStore.showError('Your are not authenticated. Please refresh the page', 10000);
        throw new Error('You are not authenticated');
      }

      const companyConfig = this.companyConfig as CompanyConfig;

      let requestUrl = companyConfig?.apiServer ?
        `https://${companyConfig.apiServer}${url}` :
        `${API_BASE_URL}${url}`;

      const response = await customRequest({
        url: requestUrl,
        params,
        method,
        headers: {
          ...headers,
          Authorization: `Bearer ${this.authToken}`,
        },
        options,
      });

      if (response.status === 401) {
        await this.attemptTokenRefresh();
        return await this.makeAuthorizedRequest(url, params, method, headers, options);
      }

      if (
        response.status === 200 &&
      (!method || method === 'GET')
      ) {
        logger.debug(`Storing info for ${url}, ${this.storeDB.dbName}`);
        try {
          await this.storeDB.init();
          await this.storeDB.save(url, response.response);
        } catch (error) {
          logger.error('Error saving to indexDB', error);
        }
      }

      if (response.status < 200 || response.status > 300) {
        logger.error(`API error request: ${response.message} `, requestUrl, response);
        throw response;
      }

      return response.response;
    };
}

export default NewAuthStore;
